import React, {useCallback, useMemo, useRef, useState} from "react";
import {canSaveQCNote, limitQCNoteLines, stripSynchronizedTagPrefix, type QCProblemTag} from "../models/constants/qcProblemTags.ts";
import {createQCNote, deleteQCNote, getQCNotes, updateQCNote} from "../services/qcNoteService.ts";
import {formatTime} from "../utils/formatTime.ts";
import {previewQCContext, type PreviewQCPlayer, type QCNoteRow} from "./previewQCContext.ts";
import {groupQCNoteRows, hasUnsavedQCNoteEdits, toQCNoteRow} from "./qcNoteRows.ts";
import {usePreviewLanguageSession} from "./usePreviewLanguageSession.ts";

export const PreviewQCProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const [notesByLanguage, setNotesByLanguage] = useState<Record<string, QCNoteRow[]>>({});
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState<string | null>(null);
  const playerRef = useRef<PreviewQCPlayer | null>(null);
  const currentTimeRef = useRef(0);
  const currentProjectIdRef = useRef<string | null>(null);
  const loadRequestRef = useRef(0);
  const savingNoteIdsRef = useRef(new Set<string>());

  const {
    isPreviewSessionActive,
    setPreviewSessionActive,
    sourceLanguageCode,
    translationLanguages,
    activeTranslationLanguage,
    selectedLanguageCode,
    setProjectLanguages,
    openTranslationLanguage,
    closeTranslationLanguage,
    resetPreviewLanguageSession,
  } = usePreviewLanguageSession();

  const setCurrentTime = useCallback((time: number) => {
    currentTimeRef.current = time;
  }, []);

  const getCurrentTime = useCallback(() => currentTimeRef.current, []);

  const loadQCNotes = useCallback(async (projectId: string) => {
    const requestId = loadRequestRef.current + 1;
    loadRequestRef.current = requestId;
    currentProjectIdRef.current = projectId;
    setCurrentProjectId(projectId);
    setNotesByLanguage({});
    setNotesLoading(true);
    setNotesError(null);
    try {
      const notes = await getQCNotes(projectId);
      if (currentProjectIdRef.current !== projectId || loadRequestRef.current !== requestId) return;
      setNotesByLanguage(groupQCNoteRows(notes));
    } catch (error) {
      if (currentProjectIdRef.current !== projectId || loadRequestRef.current !== requestId) return;
      if (error instanceof Error) {
        setNotesError("Unable to load QC notes. Try reopening this project.");
      } else {
        setNotesError("Unexpected QC note loading failure. Try reopening this project.");
      }
    } finally {
      if (currentProjectIdRef.current === projectId && loadRequestRef.current === requestId) setNotesLoading(false);
    }
  }, []);

  const getNotes = useCallback((languageCode: string) => notesByLanguage[languageCode] ?? [], [notesByLanguage]);

  const addNoteRow = useCallback((languageCode: string) => {
    const time = (playerRef.current && !playerRef.current.isDisposed?.())
      ? (playerRef.current.currentTime() ?? currentTimeRef.current)
      : currentTimeRef.current;
    const row: QCNoteRow = {
      id: crypto.randomUUID(),
      timecode: time,
      timecodeLabel: formatTime(time),
      text: "",
      problemTag: null,
      saved: false,
      saving: false,
    };
    setNotesError(null);
    setNotesByLanguage((previous) => ({
      ...previous,
      [languageCode]: [...(previous[languageCode] ?? []), row],
    }));
    return row.id;
  }, []);

  const updateNoteText = useCallback((languageCode: string, id: string, text: string) => {
    const limitedText = limitQCNoteLines(text);
    setNotesByLanguage((previous) => ({
      ...previous,
      [languageCode]: (previous[languageCode] ?? []).map((note) => note.id === id ? {...note, text: limitedText} : note),
    }));
  }, []);

  const setNoteProblemTag = useCallback((languageCode: string, id: string, problemTag: QCProblemTag | null) => {
    setNotesByLanguage((previous) => ({
      ...previous,
      [languageCode]: (previous[languageCode] ?? []).map((note) => note.id === id ? {...note, problemTag} : note),
    }));
  }, []);

  const saveNote = useCallback(async (languageCode: string, id: string) => {
    const note = (notesByLanguage[languageCode] ?? []).find((item) => item.id === id);
    if (!note || note.saving || !canSaveQCNote(note.text, note.problemTag) || !hasUnsavedQCNoteEdits(note) || savingNoteIdsRef.current.has(id)) return;
    const projectId = currentProjectIdRef.current;
    if (!projectId) {
      setNotesError("Unable to save QC note. Try Save again.");
      return;
    }
    savingNoteIdsRef.current.add(id);
    setNotesError(null);
    setNotesByLanguage((previous) => ({
      ...previous,
      [languageCode]: (previous[languageCode] ?? []).map((item) => item.id === id ? {...item, saving: true} : item),
    }));
    try {
      const normalizedText = stripSynchronizedTagPrefix(note.text, note.problemTag);
      const persisted = note.saved
        ? await updateQCNote(projectId, id, {text: normalizedText, problemTag: note.problemTag})
        : await createQCNote(projectId, note.problemTag === null
          ? {languageCode, timecodeSeconds: note.timecode, text: normalizedText}
          : {languageCode, timecodeSeconds: note.timecode, text: normalizedText, problemTag: note.problemTag});
      if (currentProjectIdRef.current !== projectId) return;
      setNotesByLanguage((previous) => ({
        ...previous,
        [languageCode]: (previous[languageCode] ?? []).map((item) => item.id === id ? toQCNoteRow(persisted) : item),
      }));
    } catch (error) {
      if (currentProjectIdRef.current !== projectId) return;
      setNotesByLanguage((previous) => ({
        ...previous,
        [languageCode]: (previous[languageCode] ?? []).map((item) => item.id === id ? {...item, saving: false} : item),
      }));
      if (error instanceof Error) {
        setNotesError("Unable to save QC note. Try Save again.");
      } else {
        setNotesError("Unexpected QC note save failure. Try Save again.");
      }
    } finally {
      savingNoteIdsRef.current.delete(id);
    }
  }, [notesByLanguage]);

  const deleteNote = useCallback(async (languageCode: string, id: string) => {
    const notes = notesByLanguage[languageCode] ?? [];
    const noteIndex = notes.findIndex((item) => item.id === id);
    const note = noteIndex === -1 ? undefined : notes[noteIndex];
    if (!note) return;
    if (!note.saved) {
      setNotesByLanguage((previous) => ({
        ...previous,
        [languageCode]: (previous[languageCode] ?? []).filter((item) => item.id !== id),
      }));
      return;
    }
    const projectId = currentProjectIdRef.current;
    if (!projectId) {
      setNotesError("Unable to delete QC note. Try Delete again.");
      return;
    }
    setNotesError(null);
    setNotesByLanguage((previous) => ({
      ...previous,
      [languageCode]: (previous[languageCode] ?? []).filter((item) => item.id !== id),
    }));
    try {
      await deleteQCNote(projectId, id);
    } catch (error) {
      if (currentProjectIdRef.current !== projectId) return;
      setNotesByLanguage((previous) => {
        const currentNotes = previous[languageCode] ?? [];
        if (currentNotes.some((item) => item.id === id)) return previous;
        const insertionIndex = Math.min(noteIndex, currentNotes.length);
        return {
          ...previous,
          [languageCode]: [...currentNotes.slice(0, insertionIndex), note, ...currentNotes.slice(insertionIndex)],
        };
      });
      if (error instanceof Error) {
        setNotesError("Unable to delete QC note. Try Delete again.");
      } else {
        setNotesError("Unexpected QC note delete failure. Try Delete again.");
      }
    }
  }, [notesByLanguage]);

  const seekToNoteTimecode = useCallback((timecode: number) => {
    const targetTime = Math.max(0, timecode - 10);
    if (playerRef.current && !playerRef.current.isDisposed?.()) {
      playerRef.current.currentTime(targetTime);
    }
    currentTimeRef.current = targetTime;
  }, []);

  const resetSession = useCallback(() => {
    loadRequestRef.current += 1;
    currentProjectIdRef.current = null;
    savingNoteIdsRef.current.clear();
    resetPreviewLanguageSession();
    currentTimeRef.current = 0;
    setNotesByLanguage({});
    setCurrentProjectId(null);
    setNotesLoading(false);
    setNotesError(null);
    playerRef.current = null;
  }, [resetPreviewLanguageSession]);

  const notesEmpty = currentProjectId !== null && !notesLoading && notesError === null && Object.values(notesByLanguage).every((notes) => notes.length === 0);

  const value = useMemo(() => ({
    isPreviewSessionActive,
    setPreviewSessionActive,
    setCurrentTime,
    getCurrentTime,
    playerRef,
    sourceLanguageCode,
    translationLanguages,
    activeTranslationLanguage,
    selectedLanguageCode,
    setProjectLanguages,
    openTranslationLanguage,
    closeTranslationLanguage,
    notesLoading,
    notesError,
    notesEmpty,
    loadQCNotes,
    getNotes,
    addNoteRow,
    updateNoteText,
    setNoteProblemTag,
    saveNote,
    deleteNote,
    seekToNoteTimecode,
    resetSession,
  }), [isPreviewSessionActive, sourceLanguageCode, translationLanguages, activeTranslationLanguage, selectedLanguageCode, notesLoading, notesError, notesEmpty, setPreviewSessionActive, setCurrentTime, getCurrentTime, setProjectLanguages, openTranslationLanguage, closeTranslationLanguage, loadQCNotes, getNotes, addNoteRow, updateNoteText, setNoteProblemTag, saveNote, deleteNote, seekToNoteTimecode, resetSession]);

  return <previewQCContext.Provider value={value}>{children}</previewQCContext.Provider>;
};
